import store from '@/store'
import {
  constantStrings,
  messageResponseTypes,
  status,
  schemaFormConstants,
  feedback,
  surveyForm
} from '@/services/constants'
import cloneDeep from 'lodash/cloneDeep'
import { getTimestampByTimezone, getTranslation } from '@/services/utils'
import { RATINGS_LABEL } from '@/views/chat-view/components/utilities/SurveyForm/constant.js'
import { fetchAgentDeskConfig, getPillInfo } from '../utils/utils'
import { getShortId } from '../../crypto/index'
import { isSensitiveDataMaskingEnabled } from '../../../store/actions/chatMessages_ac'
import AppLog from '@/services/errors/app-logs'
import Constants from '@/services/constants/constantStrings'

let secureFieldArray = []
async function generateTranscript(addUserInfo = true) {
  const chats = await getCurrentSessionChats()
  // traversing chats and mapping them with respective handlers
  let transcript = chats.reduce((transcriptTillNow, currentResponse) => {
    const sender = getSender(currentResponse)
    currentResponse?.messages?.forEach((message) => {
      const responseTypeMapper = messageResponseTypeMapper()[message.type]
      if (responseTypeMapper) {
        // find response type
        const messageResponse = responseTypeMapper(message, addUserInfo)
        if (messageResponse)
          transcriptTillNow += transcriptFormatter(
            message.timestamp,
            sender,
            messageResponse
          )
        if (isQuickReplyExists(message)) {
          transcriptTillNow += messageResponseTypeHandlers.quickReplyHandler(
            message.messageValue.quickReply
          )
        }
        if (message?.messageValue?.feedbackValue) {
          transcriptTillNow += messageResponseTypeHandlers.feedbackReview(
            message.messageValue.feedbackValue,
            getUserDisplayName()
          )
        }
      }
    })
    if (
      currentResponse.isMessageSeparator &&
      currentResponse?.content?.type === 'TERMS_AND_CONDITION_ACCEPTED'
    ) {
      transcriptTillNow += transcriptFormatter(
        currentResponse.timestamp,
        '',
        `${constantStrings.TERMS_AND_CONDITION_ACCEPTED_MESSAGE.TRANSCRIPT()}`
      )
    }
    if (
      currentResponse.isMessageSeparator &&
      currentResponse?.content?.message &&
      currentResponse?.content?.type !== 'TERMS_AND_CONDITION_ACCEPTED'
    ) {
      const handlerFunction = getPillInfo()[currentResponse.content?.type]
      if (handlerFunction) {
        transcriptTillNow += transcriptFormatter(
          currentResponse.timestamp,
          '',
          `${constantStrings.PILL_SEPERATOR} ${handlerFunction(currentResponse.content.message)} ${constantStrings.PILL_SEPERATOR}`
        )
      } else {
        transcriptTillNow += transcriptFormatter(
          currentResponse.timestamp,
          '',
          `${constantStrings.PILL_SEPERATOR} ${currentResponse.content.message} ${constantStrings.PILL_SEPERATOR}`
        )
      }
    }
    if (
      currentResponse.eventName === 'INFO_PILL' &&
      currentResponse.triggerType === 'EVENT'
    ) {
      const handlerFunction = getPillInfo()[currentResponse.eventData.subType]
      if (handlerFunction) {
        transcriptTillNow += transcriptFormatter(
          currentResponse.timestamp,
          '',
          `${constantStrings.PILL_SEPERATOR} ${handlerFunction(currentResponse?.eventData?.eventInfo)} ${constantStrings.PILL_SEPERATOR}`
        )
      }
    }
    return transcriptTillNow
  }, '')

  transcript = `${addUserInfo ? getUserInfo() : ''} ${transcript}`

  transcript = transcript.replace(/<br\s*\/?>/gi, '\n')
  return parseHTML(transcript)
}

function parseHTML(html) {
  var t = document.createElement('template')
  t.innerHTML = html
  return t.content.textContent
}

function isQuickReplyExists(message) {
  return message.messageValue?.quickReply
}

function getButtonTranscript(title, url) {
  if (url) {
    return `[ ${title}: ${url} ]`
  }
  return `[ ${title} ]`
}

function isSecureTranscriptEnabled() {
  const agentDeskConfig = fetchAgentDeskConfig()
  if (agentDeskConfig?.agentDeskChannelName === Constants.ZENDESK) {
    return Boolean(agentDeskConfig?.zendesk?.ENABLE_SECURITY_TRANSCRIPT)
  }
  return false
}

// extracts and returns current session chats
async function getCurrentSessionChats() {
  let chats
  if (isSecureTranscriptEnabled()) {
    chats = cloneDeep(await store.dispatch('receiveMaskedHistoryOfMessages'))
    new AppLog({
      name: 'getCurrentSessionChats',
      message: 'ENABLE_SECURITY_TRANSCRIPT flag is set to true',
      logLevel: 'info',
      service: 'Chat Transcript'
    })
  } else {
    chats = cloneDeep(store.state.chats)
  }
  return chats
}

function carouselAndCardElementFormatter(title, subtitle, buttons) {
  let response = ''
  if (title) response = response.concat(`\nTitle: ${title} `)
  if (subtitle) response = response.concat(`\nDetails: ${subtitle}\n`)

  const formattedOptionsField = buttons.reduce((buttonsTillNow, currButton) => {
    buttonsTillNow += getButtonTranscript(currButton.title, currButton.url)
    return buttonsTillNow
  }, '')

  if (formattedOptionsField)
    response = response.concat(`Options: ${formattedOptionsField}\n`)

  return response
}

function isTextResponseEvent(text) {
  return text.startsWith(constantStrings.SWITCH_INTENT_EVENT)
}

function isTextResponseOfSchemaForm(text) {
  return text.startsWith(constantStrings.EVENT_LEARN_ATTRIBUTE)
}

function sanitizeAnchorTagInText(text) {
  var html = document.createElement('html')
  html.innerHTML = text
  const tagArray = html.getElementsByTagName('a')
  Object.keys(tagArray)?.forEach((element) => {
    if (tagArray[element]?.innerText?.length)
      tagArray[element].innerText =
        `${tagArray[element].innerText} ( ${tagArray[element]?.attributes?.href?.value} ) `
  })
  return html.textContent
}

function getSchemaFormComponentsLabels(message, schemaFormLabels, addUserInfo) {
  let text = message.messageValue.text
  const formQue = schemaFormLabels.formPropertiesQue
  text = text.slice(31)
  let response = '\n'
  if (formQue) {
    response += `${formQue}\n`
  }
  const childComponents = text.split(/&(?!(amp))/g)
  const regex = /([;&^$^=::_])amp/g
  childComponents
    .filter((component) => component !== undefined)
    .map((component, index) => {
      let [output, value] = component.split('::value=')
      if (secureFieldArray?.[index]) {
        const unmaskedvalue =
          store.state.formInputMaskedValue[getShortId(value)] || value
        if (addUserInfo) {
          value = unmaskedvalue
        } else {
          value = maskString(unmaskedvalue)
        }
      }
      if (schemaFormLabels.version === 'v2') {
        const label = output.includes(schemaFormConstants.DEFAULT_OUTPUT_KEY)
          ? `index-${index}`
          : output
        response += `${schemaFormLabels[label]} : ${value?.replace(regex, '$1')}\n`
      } else {
        response += `${(schemaFormLabels[output] || schemaFormLabels[output?.toLowerCase()])?.toLowerCase()?.replaceAll('_', ' ')} : ${value?.replace(regex, '$1')}\n`
      }
    })
  return response
}

function getLLMSchemaFormComponentsLabels(
  message,
  schemaFormLabels,
  addUserInfo
) {
  const text = message.messageValue.label
  const formQue = schemaFormLabels.formPropertiesQue
  let response = '\n'
  if (formQue) {
    response += `${formQue}\n`
  }
  const childComponents = text?.split('\n')
  childComponents
    .filter((component) => component !== undefined)
    .map((component, index) => {
      const label = `index-${index}`
      let value = component
      if (secureFieldArray?.[index]) {
        const unmaskedvalue =
          store.state.formInputMaskedValue[getShortId(component)] || component
        if (addUserInfo) {
          value = unmaskedvalue
        } else {
          value = maskString(unmaskedvalue)
        }
      }
      response += `${schemaFormLabels[label]} : ${value}\n`
    })
  return response
}

let messageResponseTypeHandlers = {
  textResponseHandler(message, addUserInfo) {
    const { text: originalText, label: originalLabel } = message.messageValue
    const shouldMask =
      isSensitiveDataMaskingEnabled(store.state) &&
      !originalText?.startsWith('event://;') &&
      addUserInfo
    const maskedText = shouldMask
      ? store.state?.maskedValueMap?.[originalText]
      : null
    const maskedLabel = shouldMask
      ? store.state?.maskedValueMap?.[originalLabel]
      : null
    const text = maskedText ?? originalText
    const label = maskedLabel ?? originalLabel
    const schemaFormLabels = store.state.schemaFormLabels
    const isLLMSchemaForm = message.messageValue?.isLLMSchemaForm
    if (isTextResponseEvent(text)) {
      return text.slice(37).replaceAll('.', ' ')
    }
    if (isLLMSchemaForm) {
      const response = getLLMSchemaFormComponentsLabels(
        message,
        schemaFormLabels,
        addUserInfo
      )
      Object.keys(schemaFormLabels).length > 0 &&
        store.dispatch('updateSchemaFormLabels', {})
      return response
    }
    if (isTextResponseOfSchemaForm(text)) {
      const response = getSchemaFormComponentsLabels(
        message,
        schemaFormLabels,
        addUserInfo
      )
      Object.keys(schemaFormLabels).length != 0 &&
        store.dispatch('updateSchemaFormLabels', {})
      return response
    }
    return sanitizeAnchorTagInText(label || text)
  },
  fileAttachmentResponseHandler(message) {
    return message.messageValue.fileURL
  },
  imageResponseHandler(message) {
    return message.messageValue.largeImageUrl
  },
  videoResponseHandler(message) {
    return message.messageValue.thumbnailUrl
  },
  carouselResponseHandler(message) {
    return message.messageValue.elements.reduce(
      (carouselElementsResponseTillNow, currCarouselElement) => {
        carouselElementsResponseTillNow += carouselAndCardElementFormatter(
          currCarouselElement.title,
          currCarouselElement.subtitle,
          currCarouselElement.buttons
        )
        return carouselElementsResponseTillNow
      },
      ''
    )
  },
  cardResponseHandler(message) {
    const cardElement = message.messageValue
    return carouselAndCardElementFormatter(
      cardElement.title,
      cardElement.text,
      cardElement.buttons
    )
  },
  quickReplyHandler(quickReplyMessage) {
    const quickResponse = quickReplyMessage.options.reduce(
      (quickReplyTillNow, currQuickReply) => {
        quickReplyTillNow += `[${currQuickReply?.label}] `
        return quickReplyTillNow
      },
      ''
    )
    return quickResponse ? `\nOptions: ${quickResponse}\n` : ''
  },
  schemaFormResponseHandler(message) {
    const form = JSON.parse(message.messageValue.schema)[0]
    const que = form?.properties?.question
    const schema = form.schema
    const version = form.properties?.version
    let formLabels = {
      version
    }
    secureFieldArray = []
    schema.forEach((component, index) => {
      if (version === 'v2') {
        const isMandatory = component?.additionalSettings?.['Required']?.value
        secureFieldArray.push(
          component?.dropDownSelections?.['Secure field']?.value || false
        )
        formLabels[getSchemaFormFieldKeyForV2(component) || `index-${index}`] =
          `\t${component?.groupLabel || component?.labels?.[0] || component.output} ${isMandatory ? `(${getTranslation(constantStrings.MANDATORY)})` : ''}`
      } else if (component.output) {
        secureFieldArray.push(
          component?.dropDownSelections?.['Secure field']?.value || false
        )
        const isMandatory = component.validations?.find(
          ({ type }) => type === 'required'
        )?.value?.[0]
        formLabels[component.output] =
          `\t${component?.labels?.[0] || component.output} ${isMandatory ? `(${getTranslation(constantStrings.MANDATORY)})` : ''}`
      }
    })
    if (que && que.length) {
      formLabels['formPropertiesQue'] = `\t${que}`
    }
    store.dispatch('updateSchemaFormLabels', formLabels)
    return que
      ? `${constantStrings.PILL_SEPERATOR} ${getUserDisplayName()} prompted to fill the form: ${que} ${constantStrings.PILL_SEPERATOR}`
      : ''
  },
  llmSchemaFormResponseHandler(message) {
    const form = message.messageValue.schema
    const que = message.messageValue.title
    secureFieldArray = []
    const schema = form
    let formLabels = {}
    schema.forEach((component, index) => {
      const isMandatory = component.required
      secureFieldArray.push(
        (['full', 'partial'].includes(component?.maskType) &&
          component.type === 'text') ||
          false
      )
      formLabels[`index-${index}`] =
        `\t${component?.groupLabel || component?.labels?.[0] || component.output} ${isMandatory ? `(${getTranslation(constantStrings.MANDATORY)})` : ''}`
    })
    if (que && que.length) {
      formLabels['formPropertiesQue'] = `\t${que}`
    }
    store.dispatch('updateSchemaFormLabels', formLabels)
    return que
      ? `${constantStrings.PILL_SEPERATOR} ${getUserDisplayName()} prompted to fill the form: ${que} ${constantStrings.PILL_SEPERATOR}`
      : ''
  },
  humanHandoffFormResponseHandler(message) {
    const values = message?.messageValue?.values
    const formId = values?.formId?.[0]
    const formFields =
      store.state.widgetConfig?.customForms?.[formId]?.formFields
    let formData =
      `\n${store.state.widgetConfig?.customForms?.[formId]?.header}` || '\n'
    if (!values || values?.status[0] !== status.SUBMITTED || !formFields) return
    formData += formFields?.reduce((dataTillNow, currentData) => {
      if (currentData?.field in values) {
        dataTillNow += `\n${currentData?.label}: ${values[currentData?.field]?.[0]}`
        return dataTillNow
      }
    }, '')
    return formData
  },
  feedbackReview(feedbackValue, sender) {
    return `\n${constantStrings.PILL_SEPERATOR} ${sender} rated this message as: ${feedbackValue === feedback.POSITIVE ? feedback.THUMBS_UP : feedback.THUMBS_DOWN} ${constantStrings.PILL_SEPERATOR}`
  },
  surveyFormResponseHandler({ messageValue, timestamp }) {
    if (!messageValue) return
    let response = `${constantStrings.PILL_SEPERATOR} ${getUserDisplayName()} prompted to fill survey ${constantStrings.PILL_SEPERATOR}`
    response += transcriptFormatter(timestamp, getUserDisplayName(), '')
    const schema = JSON.parse(messageValue.schema)
    if (schema.isSurveySkipped) {
      response += `${constantStrings.PILL_SEPERATOR} ${getUserDisplayName()} skipped survey ${constantStrings.PILL_SEPERATOR}\n`
      return response
    } else if (schema.submitSurveyInfo) {
      response += '\n'
      const { payload, submitSurveyInfo } = schema
      const feedbackMessage = payload.feedbackMessage?.text
      let feedbackMessageValue = submitSurveyInfo?.rating
      const isSentimentNegative =
        feedbackMessageValue < payload.surveyRatingTypeEnabledInfo.criteria
      if (RATINGS_LABEL[payload.ratingTypeEnabled]) {
        feedbackMessageValue =
          RATINGS_LABEL[payload.ratingTypeEnabled][feedbackMessageValue]
      } else {
        feedbackMessageValue = `${feedbackMessageValue}/${payload.surveyRatingTypeEnabledInfo.upperBound}`
      }
      if (feedbackMessage && feedbackMessageValue) {
        response += `\t${feedbackMessage}: ${feedbackMessageValue}\n`
      }
      const resolutionQuestion = payload.resolutionQuestion?.text
      const resolutionQuestionValue = submitSurveyInfo?.issueResolved
        ? surveyForm.YES
        : surveyForm.NO
      if (resolutionQuestion) {
        response += `\t${resolutionQuestion}: ${resolutionQuestionValue}\n`
      }
      const suggestions = isSentimentNegative
        ? payload.negativeSuggestionMap.title
        : payload.positiveSuggestionMap.title
      const suggestionsValues = submitSurveyInfo?.suggestions?.join(', ')
      if (suggestions && suggestionsValues) {
        response += `\t${suggestions}: ${suggestionsValues}\n`
      }
      const additionalFeedback = payload.additionalFeedback.text
      const additionalFeedbackValue = submitSurveyInfo?.additionalFeedback
      if (additionalFeedback && additionalFeedbackValue) {
        response += `\t${additionalFeedback}: ${additionalFeedbackValue}\n`
      }
      return response
    } else {
      return `${constantStrings.PILL_SEPERATOR} ${getUserDisplayName()} prompted to fill survey ${constantStrings.PILL_SEPERATOR}`
    }
  },
  multiSourceResponseHandler({ messageValue }) {
    let tempData = ''
    if (!store.getters.isDynamicSourcesDisabled) {
      if (Boolean(messageValue) && Boolean(messageValue.text || 'Sources:')) {
        tempData += `\n${sanitizeAnchorTagInText(messageValue.text || 'Sources:')}`
      }
      if (
        Boolean(messageValue) &&
        Boolean(messageValue.multipleSourceDetails) &&
        messageValue.multipleSourceDetails.length > 0
      ) {
        let multipleSourceDetails = messageValue.multipleSourceDetails
        for (let i = 0; i < multipleSourceDetails.length; i++) {
          if (multipleSourceDetails[i].text) {
            tempData += `\n\t${multipleSourceDetails[i].text}`
          }
        }
      }
      tempData += '\n'
    }
    return tempData
  },
  secureFormResponseHandler(message) {
    const mv = message?.messageValue || {}
    const origHeading = mv.heading || 'Secure Form'
    const origDescription = mv.description || ''
    const origCTA = mv.ctaText || 'Fill Form'
    const finalHeading = mv.finalHeading
    const finalBody = mv.finalBody
    const finalCtaText = mv.finalCtaText
    const isClicked = mv.isClicked === true
    const statusVal = Array.isArray(mv.status) ? mv.status[0] : mv.status
    const normalizedStatus = (statusVal || '').toString().toUpperCase()
    const isFinal = [
      'SUBMIT',
      'SUBMITTED',
      'ERROR',
      'CANCEL',
      'CANCELLED',
      'CANCELED',
      'CLOSE',
      'CLOSED'
    ].includes(normalizedStatus)

    let response = '\n'
    const origDescSanitized = origDescription
      ? sanitizeAnchorTagInText(origDescription)
      : ''
    response += `${constantStrings.PILL_SEPERATOR} ${getUserDisplayName()} prompted to open secure form: ${origHeading} ${constantStrings.PILL_SEPERATOR}`
    if (origDescSanitized) {
      response += `\n\tDetails: ${origDescSanitized}`
    }
    if (origCTA) {
      response += `\n\tCTA: ${origCTA}`
    }
    if (isClicked) {
      response += `\n${constantStrings.PILL_SEPERATOR} ${getUserDisplayName()} clicked "${origCTA}" ${constantStrings.PILL_SEPERATOR}`
    }
    if (statusVal && !isFinal) {
      response += `\n\tForm status: ${statusVal}`
    }
    if (isFinal && (finalHeading || finalBody || finalCtaText)) {
      const finalDescSanitized = finalBody
        ? sanitizeAnchorTagInText(finalBody)
        : ''
      response += `\n${constantStrings.PILL_SEPERATOR} ${origHeading} secure form status: ${statusVal} ${constantStrings.PILL_SEPERATOR}`
      if (finalHeading) {
        response += `\n\tHeading: ${finalHeading}`
      }
      if (finalDescSanitized) {
        response += `\n\tDetails: ${finalDescSanitized}`
      }
      if (finalCtaText) {
        response += `\n\tCTA: ${finalCtaText}`
      }
    }
    return response
  }
}

function messageResponseTypeMapper() {
  const {
    TEXT_RESPONSE,
    IMAGE_RESPONSE,
    VIDEO_RESPONSE,
    CAROUSEL_RESPONSE,
    CARD_RESPONSE,
    SCHEMA_FORM_RESPONSE,
    HUMAN_HANDOFF_FORM,
    SURVEY_FORM_RESPONSE,
    FILE_ATTACHMENT,
    MULTI_SOURCE,
    LLM_SCHEMA_FORM_RESPONSE,
    SECURE_FORM_RESPONSE
  } = messageResponseTypes
  return {
    [TEXT_RESPONSE]: messageResponseTypeHandlers.textResponseHandler,
    [IMAGE_RESPONSE]: messageResponseTypeHandlers.imageResponseHandler,
    [VIDEO_RESPONSE]: messageResponseTypeHandlers.videoResponseHandler,
    [CAROUSEL_RESPONSE]: messageResponseTypeHandlers.carouselResponseHandler,
    [CARD_RESPONSE]: messageResponseTypeHandlers.cardResponseHandler,
    [SCHEMA_FORM_RESPONSE]:
      messageResponseTypeHandlers.schemaFormResponseHandler,
    [SURVEY_FORM_RESPONSE]:
      messageResponseTypeHandlers.surveyFormResponseHandler,
    [HUMAN_HANDOFF_FORM]:
      messageResponseTypeHandlers.humanHandoffFormResponseHandler,
    [FILE_ATTACHMENT]:
      messageResponseTypeHandlers.fileAttachmentResponseHandler,
    [MULTI_SOURCE]: messageResponseTypeHandlers.multiSourceResponseHandler,
    [LLM_SCHEMA_FORM_RESPONSE]:
      messageResponseTypeHandlers.llmSchemaFormResponseHandler,
    [SECURE_FORM_RESPONSE]:
      messageResponseTypeHandlers.secureFormResponseHandler
  }
}

function transcriptFormatter(timestamp, sender, text) {
  const dateLocalString = getTimestampByTimezone(
    store.state.botDetails.timezone || constantStrings.GMT,
    timestamp
  )
  return `\n\n[${dateLocalString}] ${sender} : ${text}`
}

function getSender(currentResponse) {
  if (currentResponse.isUser) {
    return getUserDisplayName()
  } else if (currentResponse.isAgent) {
    return currentResponse?.agentInfo?.displayName || constantStrings.AGENT
  }
  return store.state.widgetConfig.aiAgentName || constantStrings.NETOMI_BOT
}

function getUserDisplayName() {
  return store.state.visitorDisplayNameForAgentDesk || constantStrings.VISITOR
}

function getUserInfo() {
  let value = ''
  if (store.state.userIPAddress) value += `IP: ${store.state.userIPAddress} \n`
  if (navigator.userAgent) value += `User Agent: ${navigator.userAgent} \n`
  if (Intl)
    value += `Timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}\n`
  if (window.parent.location.href)
    value += `URL: ${window.parent.location.href} \n`
  return value
}

function maskString(str, visibleCount = 0, maskChar = '*') {
  if (str.length <= visibleCount) return str
  const visible = str.slice(0, visibleCount)
  const masked = maskChar.repeat(str.length - visibleCount)
  return visible + masked
}

function getSchemaFormFieldKeyForV2(element) {
  if (element.variableId) {
    return `var_${element.variableId}`
  }
  return element.output
}

export { generateTranscript }
